import { apiClient } from '../client';
import type { CsTicketPriority, CsTier } from './tickets.api';

/**
 * Client for the CS routing admin surface (Phase 16 / ADR-026):
 *
 *   - `/api/cs/routing/rules`        (rules CRUD + reorder + dry-run)
 *   - `/api/cs/routing/groups`       (groups + members + fallback)
 *   - `/api/cs/routing/sla-policies` (org SLA policy)
 *   - `/api/cs/routing/tags`         (tag taxonomy)
 *   - `/api/cs/orgs`                 (KwcOrgLink — brand filter source)
 *
 * Mirrors server/src/routes/cs/{routing,groups,sla,tags,kwc-org-links}
 * .routes.ts and modules/cs/routing/dsl.ts.
 */

// ── Rule DSL (mirror of modules/cs/routing/dsl.ts) ──────────────────────

export const RULE_MATCHER_FIELDS = [
  'subject_contains',
  'body_contains',
  'customer_email_is',
  'customer_email_domain',
  'customer_org_slug',
  'customer_org_slug_not',
  'tag_includes',
  'tag_not_includes',
  'priority_is',
  'status_is',
  'brand_is',
  'inbox_is',
  'assignee_empty',
  'group_empty',
] as const;

export type RuleMatcherField = (typeof RULE_MATCHER_FIELDS)[number];

export interface RuleMatcher {
  field: RuleMatcherField;
  /** Omitted for assignee_empty / group_empty; numbers for inbox_is. */
  values?: (string | number)[];
}

export interface RuleConditions {
  match: 'all' | 'any';
  matchers: RuleMatcher[];
  orgExcludeGroup?: {
    orgSlug: string;
    excludedGroupSlugs: string[];
    exclusionAssigneeEmail?: string;
  };
}

export const RULE_ACTION_TYPES = [
  'assign_group',
  'assign_membership',
  'add_tags',
  'set_priority',
  'set_tier',
  'auto_resolve',
  'spam_close',
  'stop_processing',
] as const;

export type RuleActionType = (typeof RULE_ACTION_TYPES)[number];

export type RuleAction =
  | { type: 'assign_group'; groupSlug: string }
  | { type: 'assign_membership'; membershipId?: string; membershipEmail?: string }
  | { type: 'add_tags'; tags: string[] }
  | { type: 'set_priority'; priority: CsTicketPriority }
  | { type: 'set_tier'; tier: CsTier }
  | { type: 'auto_resolve' }
  | { type: 'spam_close'; tag?: string }
  | { type: 'stop_processing' };

export interface CsRoutingRule {
  id: string;
  organizationId: string;
  name: string;
  position: number;
  active: boolean;
  isSystem: boolean;
  stopAfterMatch: boolean;
  conditionsJson: RuleConditions;
  actionsJson: RuleAction[];
  createdByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRoutingRules(): Promise<CsRoutingRule[]> {
  const res = await apiClient.get<{ data: CsRoutingRule[] }>('/api/cs/routing/rules');
  return res.data.data;
}

export interface SaveRuleInput {
  name: string;
  position?: number;
  active?: boolean;
  stopAfterMatch?: boolean;
  conditions: unknown;
  actions: unknown;
}

export async function createRoutingRule(input: SaveRuleInput): Promise<CsRoutingRule> {
  const res = await apiClient.post<{ data: CsRoutingRule }>('/api/cs/routing/rules', input);
  return res.data.data;
}

export async function updateRoutingRule(id: string, input: Partial<SaveRuleInput>): Promise<CsRoutingRule> {
  const res = await apiClient.put<{ data: CsRoutingRule }>(
    `/api/cs/routing/rules/${encodeURIComponent(id)}`,
    input,
  );
  return res.data.data;
}

export async function deleteRoutingRule(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/routing/rules/${encodeURIComponent(id)}`);
}

/** Full-list contract: send EVERY rule id in the desired order. */
export async function reorderRoutingRules(ruleIds: string[]): Promise<CsRoutingRule[]> {
  const res = await apiClient.post<{ data: CsRoutingRule[] }>('/api/cs/routing/rules/reorder', { ruleIds });
  return res.data.data;
}

export interface DryRunRuleResult {
  ruleId: string;
  name: string;
  position: number;
  active: boolean;
  matchCount: number;
  sampleTicketIds: string[];
  invalidConditions: boolean;
}

export interface DryRunResult {
  sampleSize: number;
  ticketsEvaluated: number;
  rules: DryRunRuleResult[];
}

export async function dryRunRoutingRules(sampleSize?: number): Promise<DryRunResult> {
  const res = await apiClient.post<{ data: DryRunResult }>(
    '/api/cs/routing/rules/dry-run',
    sampleSize !== undefined ? { sampleSize } : {},
  );
  return res.data.data;
}

// ── Groups ──────────────────────────────────────────────────────────────

export interface CsGroupMember {
  id: string;
  organizationId: string;
  groupId: string;
  membershipId: string;
  isDefault: boolean;
  membership?: { id: string; user: { firstName: string; lastName: string; email: string } };
}

export interface CsGroup {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  chatwootTeamId: number | null;
  brand: string | null;
  fallbackMembershipId: string | null;
  fallbackMembership: { id: string; user: { firstName: string; lastName: string; email: string } } | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  members: CsGroupMember[];
}

export async function listCsGroups(): Promise<CsGroup[]> {
  const res = await apiClient.get<{ data: CsGroup[] }>('/api/cs/routing/groups');
  return res.data.data;
}

export interface SaveGroupInput {
  name: string;
  slug: string;
  brand?: string | null;
  chatwootTeamId?: number | null;
  fallbackMembershipId?: string | null;
  active?: boolean;
}

export async function createCsGroup(input: SaveGroupInput): Promise<CsGroup> {
  const res = await apiClient.post<{ data: CsGroup }>('/api/cs/routing/groups', input);
  return res.data.data;
}

export async function updateCsGroup(id: string, input: Partial<SaveGroupInput>): Promise<CsGroup> {
  const res = await apiClient.put<{ data: CsGroup }>(
    `/api/cs/routing/groups/${encodeURIComponent(id)}`,
    input,
  );
  return res.data.data;
}

export async function deleteCsGroup(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/routing/groups/${encodeURIComponent(id)}`);
}

export async function addCsGroupMember(
  groupId: string,
  membershipId: string,
  isDefault?: boolean,
): Promise<CsGroupMember> {
  const res = await apiClient.post<{ data: CsGroupMember }>(
    `/api/cs/routing/groups/${encodeURIComponent(groupId)}/members`,
    { membershipId, ...(isDefault !== undefined ? { isDefault } : {}) },
  );
  return res.data.data;
}

export async function removeCsGroupMember(groupId: string, membershipId: string): Promise<void> {
  await apiClient.delete(
    `/api/cs/routing/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(membershipId)}`,
  );
}

// ── SLA policies ────────────────────────────────────────────────────────

export interface CsSlaPolicy {
  id: string;
  organizationId: string;
  /** Null = the org-wide default policy. */
  priority: CsTicketPriority | null;
  firstReplyTargetMinutes: number;
  nudgeAfterMinutes: number;
  escalateAfterMinutes: number;
  active: boolean;
  updatedAt: string;
}

export async function listSlaPolicies(): Promise<CsSlaPolicy[]> {
  const res = await apiClient.get<{ data: CsSlaPolicy[] }>('/api/cs/routing/sla-policies');
  return res.data.data;
}

export interface PutSlaPolicyInput {
  priority: CsTicketPriority | null;
  firstReplyTargetMinutes?: number;
  nudgeAfterMinutes?: number;
  escalateAfterMinutes?: number;
  active?: boolean;
}

export async function putSlaPolicy(input: PutSlaPolicyInput): Promise<CsSlaPolicy> {
  const res = await apiClient.put<{ data: CsSlaPolicy }>('/api/cs/routing/sla-policies', input);
  return res.data.data;
}

// ── Tag taxonomy ────────────────────────────────────────────────────────

export interface CsTagDef {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  tier: CsTier;
  category: string;
  sopRef: string | null;
  active: boolean;
}

export async function listCsTags(): Promise<CsTagDef[]> {
  const res = await apiClient.get<{ data: CsTagDef[] }>('/api/cs/routing/tags');
  return res.data.data;
}

export interface SaveTagInput {
  name: string;
  description: string;
  tier: CsTier;
  category: string;
  sopRef?: string | null;
  active?: boolean;
}

export async function createCsTag(input: SaveTagInput): Promise<CsTagDef> {
  const res = await apiClient.post<{ data: CsTagDef }>('/api/cs/routing/tags', input);
  return res.data.data;
}

export async function updateCsTag(id: string, input: Partial<SaveTagInput>): Promise<CsTagDef> {
  const res = await apiClient.put<{ data: CsTagDef }>(
    `/api/cs/routing/tags/${encodeURIComponent(id)}`,
    input,
  );
  return res.data.data;
}

/** Deactivates (active=false) — names stay referenced by ticket history. */
export async function deactivateCsTag(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/routing/tags/${encodeURIComponent(id)}`);
}

// ── KWC org links (brand filter source) ─────────────────────────────────

export interface KwcOrgLink {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  emailDomains: string[];
  kwcOrgId: string | null;
  tags: string[];
  canViewOrgTickets: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function listKwcOrgLinks(): Promise<KwcOrgLink[]> {
  const res = await apiClient.get<{ data: KwcOrgLink[] }>('/api/cs/orgs');
  return res.data.data;
}

export interface SaveKwcOrgLinkInput {
  name: string;
  slug: string;
  emailDomains?: string[];
  kwcOrgId?: string | null;
  tags?: string[];
  canViewOrgTickets?: boolean;
  active?: boolean;
}

export async function createKwcOrgLink(input: SaveKwcOrgLinkInput): Promise<KwcOrgLink> {
  const res = await apiClient.post<{ data: KwcOrgLink }>('/api/cs/orgs', input);
  return res.data.data;
}

export async function updateKwcOrgLink(id: string, input: Partial<SaveKwcOrgLinkInput>): Promise<KwcOrgLink> {
  const res = await apiClient.put<{ data: KwcOrgLink }>(`/api/cs/orgs/${encodeURIComponent(id)}`, input);
  return res.data.data;
}

export async function deleteKwcOrgLink(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/orgs/${encodeURIComponent(id)}`);
}
